import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { FaClone } from "react-icons/fa6";
import { MdRotateLeft } from "react-icons/md";
import { Link, useNavigate } from "@tanstack/react-router";
import { TooltipTrigger } from "react-aria-components";
import { HStack, Wrap } from "../../../../styled-system/jsx";
import { css, cx } from "../../../../styled-system/css";
import { Button, buttonRecipe } from "../../core/basic/Button";
import { Tooltip } from "../../core/basic/Tooltip";
import { RT8ColumnSelector } from "../../core/blocks/MetricSelector";
import { SearchInput } from "../../fields/base/TextField";
import { BulkActionButton } from "../../table/BulkActionButton";
import { type AppColumns, createAppColumnHelper, useClientTable } from "../../table/tableFeatures";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { commaSearchFilterFn } from "../../table/filterFns";
import { resetTable } from "../../table/utils";
import { RT8Pagination } from "../../table/Pagination";
import { Table } from "../../table/Table";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { formatNumber } from "../../../utils/formatNumber";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { useDeletePrograms } from "../../../modules/programs/api/useDeletePrograms";
import { type ProgramWithActiveAds } from "./bidding/useProgramsWithActiveAdCounts";

const columnHelper = createAppColumnHelper<ProgramWithActiveAds>();

const searchFilterFn = commaSearchFilterFn<ProgramWithActiveAds>((row) => row.name);

const truncateClassName = css({
  display: "block",
  maxWidth: "100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  textAlign: "start",
});

interface Props {
  /** undefined while loading. */
  programs: ProgramWithActiveAds[] | undefined;
  isLoading: boolean;
  /** Heads the name column and names the bulk delete action. */
  title: string;
  persistKey: string;
  /** Makes the name a button that opens the program in place, instead of a link to its edit page. Keep it stable. */
  onOpenProgram?: (id: number) => void;
  /** Whether the user may change these programs: selection, bulk delete, `toolbarAction` and cloning. */
  canEdit: boolean;
  /** Bidding: rows must come from useProgramsWithActiveAdCounts. */
  showActiveAds?: boolean;
  /** Bidding and budget. Shown only when `canEdit`. */
  showClone?: boolean;
  toolbarAction?: ReactNode;
}

/** Bidding, budget and line item programs: the advertising settings tabs and the ad plan editor. */
export const AdvertisingProgramsTable = ({
  programs,
  isLoading,
  title,
  persistKey,
  onOpenProgram,
  canEdit,
  showActiveAds = false,
  showClone = false,
  toolbarAction = null,
}: Props) => {
  const { t } = useTranslation(["programs", "common"]);
  const navigate = useNavigate();
  const dateFormatter = useDateFormatter("medium");
  const hasMultiOrgRulesModuleEnabled = useModuleEnabled("MULTI_ORG_RULES");
  const deletePrograms = useDeletePrograms();

  const columns = useMemo(() => {
    const formatDate = (value: string | undefined) => (value ? dateFormatter.format(moment(value).toDate()) : "-");

    const allColumns: AppColumns<ProgramWithActiveAds> = [
      columnHelper.accessor("name", {
        header: title,
        size: 300,
        enableHiding: false,
        sortFn: "alphanumeric",
        cell: ({ getValue, row }) =>
          onOpenProgram ?
            <Button variant="link" className={truncateClassName} onPress={() => onOpenProgram(row.original.id)}>
              {getValue()}
            </Button>
          : <Link
              to="/user/advertising/programs/edit/$id"
              params={{ id: String(row.original.id) }}
              className={cx(buttonRecipe({ variant: "link" }), truncateClassName)}
            >
              {getValue()}
            </Link>,
      }),
      columnHelper.accessor("start_date", {
        header: t(($) => $.programs.header["Start Date"]),
        size: 140,
        enableResizing: false,
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      columnHelper.accessor((row) => row.end_date ?? undefined, {
        id: "end_date",
        header: t(($) => $.programs.header["End Date"]),
        size: 140,
        enableResizing: false,
        sortUndefined: "last",
        cell: ({ getValue }) => formatDate(getValue()),
      }),
      ...(hasMultiOrgRulesModuleEnabled ?
        [
          columnHelper.accessor((row) => row.org.name, {
            id: "org_name",
            header: t(($) => $.programs.list["Source Organization"]),
            size: 220,
            sortFn: "alphanumeric",
          }),
        ]
      : []),
      ...(showActiveAds ?
        [
          columnHelper.accessor((row) => row.active_ad_type_count ?? 0, {
            id: "active_ad_type_count",
            header: t(($) => $.programs.list["Active Advertisements"]),
            size: 190,
            enableResizing: false,
            meta: { justify: "end" },
            cell: ({ getValue }) => formatNumber(getValue(), { decimalPlaces: 0 }),
          }),
        ]
      : []),
      ...(canEdit && showClone ?
        [
          columnHelper.display({
            id: "actions",
            header: t(($) => $.programs.list.Actions),
            size: 100,
            enableResizing: false,
            meta: { justify: "center" },
            cell: ({ row }) => (
              <TooltipTrigger delay={0}>
                <Button
                  variant="link"
                  aria-label={t(($) => $.programs.list["Create new Rules using this as a template"])}
                  onPress={() => {
                    const program = row.original;
                    void navigate({
                      to: "/user/advertising/programs/create",
                      search: { engine: program.engine, clone: "true" },
                      state: {
                        defaults: {
                          id: undefined,
                          name: `${program.name} - Copy`,
                          ui_state: program.ui_state,
                          ...program.spec,
                          start_date: program.start_date,
                          end_date: program.end_date,
                        },
                      },
                    });
                  }}
                >
                  <FaClone />
                  <Tooltip offset={10}>
                    {t(($) => $.programs.list["Create new Rules using this as a template"])}
                  </Tooltip>
                </Button>
              </TooltipTrigger>
            ),
          }),
        ]
      : []),
    ];
    return allColumns;
  }, [
    t,
    navigate,
    dateFormatter,
    hasMultiOrgRulesModuleEnabled,
    title,
    onOpenProgram,
    showActiveAds,
    canEdit,
    showClone,
  ]);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey,
    initialState: { sorting: [{ id: "name", desc: false }] },
  });

  const table = useClientTable({
    data: isLoading ? undefined : programs,
    columns,
    getRowId: (row) => String(row.id),
    enableRowSelection: canEdit,
    globalFilterFn: searchFilterFn,
    initialState,
    state,
    ...setters,
    meta: {
      showSkeleton: isLoading,
    },
  });

  const selectedPrograms = table.getSelectedRowModel().rows.map((row) => row.original.id);

  return (
    <>
      {canEdit && (
        <Wrap gap="medium" justify="end">
          {toolbarAction}
          <BulkActionButton
            selected={selectedPrograms}
            options={[
              {
                label: t(($) => $.programs.list["Delete {title}"], { title }),
                action: () => {
                  deletePrograms.mutate(selectedPrograms, { onSuccess: () => table.resetRowSelection() });
                },
                confirmationMessage: (
                  <p>
                    {t(($) => $.programs.list["Are you sure you want to delete the selected programs?"], {
                      count: selectedPrograms.length,
                    })}
                  </p>
                ),
              },
            ]}
          />
        </Wrap>
      )}
      <HStack padding={"small"} fontSize={"large"}>
        <Button
          variant={"contentOnly"}
          aria-label={t(($) => $.common.table["Reset table filters"])}
          onPress={() => resetTable(table)}
          fontSize={"xxlarge"}
        >
          <MdRotateLeft />
        </Button>
        <RT8ColumnSelector table={table} />
        <SearchInput
          aria-label={t(($) => $.common.actions["Search"])}
          isExpandable
          css={{ flex: "0 1 350px" }}
          value={table.state.globalFilter ?? ""}
          onChange={table.setGlobalFilter}
        />
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} />
    </>
  );
};
